/**
 * API 526 표준 오리피스 데이터 로더.
 *
 * 원본 데이터: /data/api526-orifices.json
 *   - API Standard 526, 4판(1995) Table 1 및 Table 2~15 에서 판독
 *   - 면적값은 API 520 Part I 10판(2020) 예제 3건과 교차검증 완료
 *   - 데이터 수정 시 `python data/verify-api526.py` 반드시 재실행
 *
 * 이 파일은 JSON 에 타입만 입힌다. 값을 여기서 가공하지 말 것 —
 * 표준 원문과 코드 사이에 변환 단계가 끼면 추적이 불가능해진다.
 */
import raw from "../../data/api526-orifices.json";

/** 오리피스별로 제공되는 밸브 규격 한 가지. */
export interface Api526ValveSize {
  /** 예: "1D2", "1-1/2G3", "8T10" — (입구 NPS)(오리피스 문자)(출구 NPS) */
  readonly size: string;
  /** 입구 호칭경 (inch) */
  readonly inletNps: number;
  /** 출구 호칭경 (inch) */
  readonly outletNps: number;
  /** 입구 ANSI 플랜지 클래스 */
  readonly inletClass: number;
  /** 출구 ANSI 플랜지 클래스 */
  readonly outletClass: number;
}

/** API 526 표준 오리피스 1종. */
export interface Api526Orifice {
  /** 문자 지정 D~T. I 와 O 는 숫자 1·0 과 혼동되므로 존재하지 않는다. */
  readonly designation: string;
  /** 유효 오리피스 면적 (in²) — Table 1 원문 값 */
  readonly effectiveAreaIn2: number;
  /** 유효 오리피스 면적 (mm²) — 1 in² = 645.16 mm² 환산값 */
  readonly effectiveAreaMm2: number;
  readonly valveSizes: readonly Api526ValveSize[];
}

/** 면적 오름차순(D → T)으로 정렬된 표준 오리피스 목록. */
export const API_526_ORIFICES: readonly Api526Orifice[] = Object.freeze(
  [...raw.orifices].sort((a, b) => a.effectiveAreaMm2 - b.effectiveAreaMm2),
);

/** 가장 작은 표준 오리피스 (D, 0.110 in²). */
export const SMALLEST_ORIFICE = API_526_ORIFICES[0]!;

/** 가장 큰 표준 오리피스 (T, 26.0 in²). */
export const LARGEST_ORIFICE = API_526_ORIFICES[API_526_ORIFICES.length - 1]!;

/** in² → mm² 환산 계수. */
export const IN2_TO_MM2 = raw.areaUnits.in2ToMm2;

/** 문자 지정으로 오리피스를 찾는다. 없으면 undefined. */
export function findOrificeByDesignation(
  designation: string,
): Api526Orifice | undefined {
  const key = designation.trim().toUpperCase();
  return API_526_ORIFICES.find((o) => o.designation === key);
}
